import React from "react";
import { Select } from "antd";
import {
  formatDateInput,
  normalizeDateInputOnBlur,
} from "../../../utils/ddMmDateInputFormat";
import {
  buildFlightNationalityOptionsWithFallback,
  filterFlightNationalityOption,
} from "../../../utils/countries/flightNationalityOptions";
import {
  FLIGHT_PASSENGER_DATE_FIELD_KEYS,
  flightPassengerDateHintKey,
  getFlightPassengerFormFields,
} from "./flightPassengersFormShared";
import { getEnglishOnlyPersonNameError } from "../../../utils/passengers/englishOnlyPersonName";

const NAME_FIELD_KEYS = new Set(["firstName", "lastName"]);

function FlightPassengerField({
  field: f,
  passenger: p,
  index: idx,
  onPatch,
  dateFieldErrors,
  setDateFieldErrors,
}) {
  if (f.type === "select") {
    return (
      <label key={f.key} className="fw-ui-field">
        <span className="fw-ui-field-label">
          {f.label}
          {f.required ? " *" : ""}
        </span>
        <select
          className="fw-ui-input"
          value={p[f.key] || ""}
          onChange={(e) => onPatch(idx, f.key, e.target.value)}
        >
          <option value="">בחרו</option>
          <option value="M">זכר</option>
          <option value="F">נקבה</option>
          <option value="X">אחר</option>
        </select>
      </label>
    );
  }
  if (FLIGHT_PASSENGER_DATE_FIELD_KEYS.has(f.key)) {
    const hint = flightPassengerDateHintKey(idx, f.key);
    return (
      <label key={f.key} className="fw-ui-field">
        <span className="fw-ui-field-label">
          {f.label}
          {f.required ? " *" : ""}
        </span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          className="fw-ui-input"
          value={p[f.key] || ""}
          placeholder={f.placeholder}
          onChange={(e) => {
            const next = formatDateInput(e.target.value);
            onPatch(idx, f.key, next);
            setDateFieldErrors((prev) => {
              if (!prev[hint]) return prev;
              const copy = { ...prev };
              delete copy[hint];
              return copy;
            });
          }}
          onBlur={(e) => {
            const { normalized, error } = normalizeDateInputOnBlur(
              e.target.value
            );
            if (!error && normalized) {
              onPatch(idx, f.key, normalized);
            }
            setDateFieldErrors((prev) => {
              const copy = { ...prev };
              if (error) copy[hint] = error;
              else delete copy[hint];
              return copy;
            });
          }}
        />
        {dateFieldErrors[hint] ? (
          <p
            className="fw-ui-field-hint fw-ui-field-hint--error"
            role="status"
          >
            {dateFieldErrors[hint]}
          </p>
        ) : null}
      </label>
    );
  }
  if (f.type === "nationality") {
    const rawNat = p[f.key];
    const natOptions = buildFlightNationalityOptionsWithFallback(rawNat);
    const selectValue = (() => {
      if (rawNat == null || String(rawNat).trim() === "") {
        return undefined;
      }
      const u = String(rawNat).toUpperCase();
      if (natOptions.some((o) => o.value === u)) return u;
      return undefined;
    })();
    return (
      <label key={f.key} className="fw-ui-field fw-ui-field--nationality">
        <span className="fw-ui-field-label">
          {f.label}
          {f.required ? " *" : ""}
        </span>
        <Select
          showSearch
          allowClear
          placeholder="בחר אזרחות"
          className="fw-nationality-select"
          classNames={{
            popup: { root: "fw-nationality-select__popup" },
          }}
          optionFilterProp="label"
          filterOption={filterFlightNationalityOption}
          value={selectValue}
          onChange={(v) => onPatch(idx, f.key, v ?? "")}
          options={natOptions}
          style={{ width: "100%" }}
          listHeight={280}
        />
      </label>
    );
  }
  const nameError = NAME_FIELD_KEYS.has(f.key)
    ? getEnglishOnlyPersonNameError(p[f.key])
    : null;
  return (
    <label key={f.key} className="fw-ui-field">
      <span className="fw-ui-field-label">
        {f.label}
        {f.required ? " *" : ""}
      </span>
      <input
        type={f.type}
        className="fw-ui-input"
        value={p[f.key] || ""}
        placeholder={f.placeholder}
        autoComplete={f.autoComplete}
        onChange={(e) => onPatch(idx, f.key, e.target.value)}
        aria-invalid={nameError ? true : undefined}
      />
      {nameError ? (
        <p className="fw-ui-field-hint fw-ui-field-hint--error" role="alert">
          {nameError}
        </p>
      ) : null}
    </label>
  );
}

/**
 * גוף טופס נוסעים — משותף לשלב נוסעים ולעריכה מסיכום review.
 */
export default function FlightPassengersFormBody({
  passengers,
  onPatch,
  dateFieldErrors,
  setDateFieldErrors,
  requirePassport = true,
}) {
  const fields = getFlightPassengerFormFields({ requirePassport });
  const coreFields = fields.filter((f) => !f.primaryOnly);
  const contactFields = fields.filter((f) => f.primaryOnly);
  const coreGridClass = requirePassport
    ? "fw-ui-form-grid fw-form-grid"
    : "fw-ui-form-grid fw-form-grid fw-form-grid--pax-core";

  return (
    <div className="fw-ui-pax-list fw-pax-list">
      {passengers.map((p, idx) => (
        <section key={idx} className="fw-ui-pax-section fw-pax-section">
          <h3 className="fw-ui-pax-title">נוסע {idx + 1}</h3>
          <div className={coreGridClass}>
            {coreFields.map((f) => (
              <FlightPassengerField
                key={f.key}
                field={f}
                passenger={p}
                index={idx}
                onPatch={onPatch}
                dateFieldErrors={dateFieldErrors}
                setDateFieldErrors={setDateFieldErrors}
              />
            ))}
          </div>
          {idx === 0 && contactFields.length > 0 ? (
            <div className="fw-ui-form-grid fw-form-grid fw-form-grid--pax-contact">
              {contactFields.map((f) => (
                <FlightPassengerField
                  key={f.key}
                  field={f}
                  passenger={p}
                  index={idx}
                  onPatch={onPatch}
                  dateFieldErrors={dateFieldErrors}
                  setDateFieldErrors={setDateFieldErrors}
                />
              ))}
            </div>
          ) : null}
        </section>
      ))}
    </div>
  );
}
